'use client';

import useSWR from 'swr';

import axiosInstance, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

/** Måste spegla `ÅtgärdDto` i backend. */
export type Åtgärd = {
  typ: string;
  rubrik: string;
  antal: number;
  /** Relativ sökväg utan organisationsprefix — se `ROOTS.DASHBOARD = ""`. */
  länk: string;
  allvarlighetsgrad: 'INFO' | 'VARNING' | 'BRÅDSKANDE';
};

/**
 * Måste spegla `NyckeltalDto`. Varje fält är nullbart med flit: ett nyckeltal som
 * inte gick att räkna ska visas som frånvarande och inte som noll.
 */
export type Nyckeltal = {
  antalIInkorgen: number | null;
  antalObokförda: number | null;
  antalOmatchadeInköp: number | null;
};

export type Organisationsöversikt = {
  orgId: string;
  orgNamn: string | null;
  organisationsTyp: string | null;
  åtgärder: Åtgärd[];
  nyckeltal: Nyckeltal | null;
  /** Satt när just den föreningen inte kunde läsas. De övriga är ändå giltiga. */
  fel: string | null;
};

export type AllaÖversikt = {
  organisationer: Organisationsöversikt[];
  antalÖppnaÅtgärder: number;
};

// ----------------------------------------------------------------------

const TOM: AllaÖversikt = { organisationer: [], antalÖppnaÅtgärder: 0 };

async function hämtare(url: string): Promise<AllaÖversikt> {
  const svar = await axiosInstance.get<AllaÖversikt>(url);
  return svar.data ?? TOM;
}

/**
 * Hubbens data: en rad per förening användaren är medlem i.
 *
 * Anropet skickar medvetet ingen organisation. Hubbläget har ingen vald förening,
 * och axios-interceptorn utelämnar då `X-Organisation-Id` helt — vilka föreningar
 * som ingår avgör backend utifrån vem som är inloggad.
 */
export function useAllaÖversikt(aktiv: boolean = true) {
  const { data, isLoading, error, mutate } = useSWR<AllaÖversikt>(
    aktiv ? endpoints.översikt.alla : null,
    hämtare,
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  return {
    översikt: data ?? TOM,
    översiktLoading: isLoading,
    översiktFel: error,
    uppdateraÖversikt: mutate,
  };
}
